import React, { useState, useEffect } from "react";

export const isElectron = typeof navigator !== "undefined" && navigator.userAgent.toLowerCase().includes("electron");

// Live connectivity signal for the desktop app: setup/roster data (players,
// groups, teams, course) is cloud-authoritative, so editing it offline would
// silently be discarded on the next Sync Now pull. navigator.onLine reflects
// whether the OS network adapter is up — a good-enough proxy for "can this
// reach the cloud" in a no-signal-on-the-course context, without needing a
// round trip to the server just to check.
export default function useIsOnline() {
  const [isOnline, setIsOnline] = useState(typeof navigator !== "undefined" ? navigator.onLine : true);

  useEffect(() => {
    const goOnline = () => setIsOnline(true);
    const goOffline = () => setIsOnline(false);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, []);

  return isOnline;
}

// Players/Groups/Teams: informational only. These screens are fully
// editable offline now -- edits are cached locally and queued, and the
// global sync ribbon (Layout.jsx) already shows the pending-change count,
// so this stays short and doesn't repeat that count itself.
export function OfflineViewingCachedDataBanner({ screen }) {
  return (
    <div style={{background:"#eff6ff",borderBottom:"1px solid #bfdbfe",color:"#1e40af",padding:"8px 16px",fontSize:"13px",fontWeight:"600",display:"flex",alignItems:"center",gap:"8px"}}>
      <span>🌐</span>
      <span>Offline — {screen} is showing the last data loaded from the cloud. You can keep editing; changes will sync automatically when you're reconnected.</span>
    </div>
  );
}

// Course.jsx only: this screen stays read-only offline (editing course
// setup is a before-the-round task with plenty of signal, not a
// stand-on-the-course workflow) -- keeps the original blocking framing.
export function OfflineSetupBanner({ screen }) {
  return (
    <div style={{background:"#fffbeb",borderBottom:"1px solid #fde68a",color:"#92400e",padding:"8px 16px",fontSize:"13px",fontWeight:"600",display:"flex",alignItems:"center",gap:"8px"}}>
      <span>🌐</span>
      <span>You're offline — {screen} can't be edited right now (it's set from the cloud). Reconnect and this will unlock automatically.</span>
    </div>
  );
}
